/**
 * Explosions and combat effects (GDD §3.7; concept L956–L999, L1022, L1063, L1096, L1424–L1432): an additive
 * fireball ramping white-yellow → orange → red, sparks falling under gravity, rising smoke, a water splash near the
 * surface, an orange light flash and camera shake; flak puffs, muzzle flashes, bridge-hit sparks, smoke from sinking
 * boats and the broken bridge, splashes of falling helicopters and spans. Every emission is the concept's, draw for
 * draw, from the effects stream; each call site reuses one spec, so nothing is created per effect.
 */
import type { Rng } from '../core/rng.ts';
import type { Fx } from '../sim/fx.ts';
import type { ParticleSpec, ParticleSystems } from './particles.ts';

const TAU = Math.PI * 2;
/** Explosion light: 30,000 cd per unit of blast, 6 m above it, fading at 5/s (concept L968, L1185). */
const LIGHT_PER_BLAST = 30000;
const LIGHT_LIFT = 6;
const LIGHT_FADE = 5;

export interface ExplosionLight {
  x: number;
  y: number;
  z: number;
  intensity: number;
}

export interface Explosions extends Fx {
  readonly light: ExplosionLight;
  /** Floating-origin shift of the light (GDD rules). */
  shiftZ(dz: number): void;
}

function spec(kind: ParticleSpec['kind'], extra: Partial<ParticleSpec>): ParticleSpec {
  return { kind, x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0, life: 0, s0: 0, s1: 0, ...extra };
}

/**
 * @param shake called with each blast's size (the top camera rig adds 0.5 × size, capped at 1.6).
 */
export function createExplosions(p: ParticleSystems, rand: Rng, shake: (size: number) => void): Explosions {
  const light: ExplosionLight = { x: 0, y: 0, z: 0, intensity: 0 };
  // One spec per call site (concept object literals), so optional fields never leak between sites.
  const boomFire = spec('fire', { drag: 2.4, grav: 2, a0: 0.85 });
  const boomSpark = spec('spark', { drag: 0.8, grav: -16, s0: 0.6, s1: 0.2 });
  const boomSmoke = spec('smoke', { drag: 0.5, grav: 0.8, r: 0.24, g: 0.22, b: 0.2, a0: 0.42 });
  const splashSmall = spec('mist', { y: 0.3, drag: 0.8, grav: -14, s0: 1.5, s1: 5, a0: 0.55, bright: 1.2, floor: 0.2 });
  const splashBig = spec('mist', { y: 0.4, drag: 0.7, grav: -14, s0: 2.5, s1: 8, a0: 0.55, bright: 1.25, floor: 0.2 });
  const flakSmoke = spec('smoke', { drag: 1.5, grav: 0.3, s0: 2.5, s1: 8, r: 0.16, g: 0.15, b: 0.15, a0: 0.6 });
  const flakFire = spec('fire', { drag: 4, grav: 0, s0: 2, s1: 4.5, a0: 0.9 });
  const muzzleSpark = spec('spark', { vy: 2, drag: 3, grav: 0, life: 0.25, s0: 3, s1: 1 });
  const hitSpark = spec('spark', { drag: 1, grav: -14, s0: 0.6, s1: 0.2 });
  const sinkSmokeSpec = spec('smoke', {
    y: 2,
    vz: 1,
    drag: 0.4,
    grav: 0.6,
    s0: 2.5,
    s1: 9,
    r: 0.2,
    g: 0.19,
    b: 0.18,
    a0: 0.3,
  });
  const bridgeSmokeSpec = spec('smoke', {
    vz: 1,
    drag: 0.4,
    grav: 0.8,
    s0: 4,
    s1: 18,
    r: 0.22,
    g: 0.2,
    b: 0.19,
    a0: 0.45,
  });

  /** concept splashSmall2 (L970–L975). */
  function splash(x: number, z: number, s: number): void {
    const sp = splashSmall;
    for (let i = 0; i < 40 * s; i++) {
      const a = rand() * TAU;
      const v = 2 + rand() * 7;
      sp.x = x + (rand() - 0.5) * 4;
      sp.z = z + (rand() - 0.5) * 4;
      sp.vx = Math.cos(a) * v;
      sp.vy = 6 + rand() * 12;
      sp.vz = Math.sin(a) * v;
      sp.life = 0.9 + rand() * 0.8;
      p.splash.emit(sp);
    }
  }

  const fx: Explosions = {
    light,
    /** concept boom (L956–L969). */
    boom(x, y, z, s) {
      const n = Math.round(60 * s);
      const f = boomFire;
      for (let i = 0; i < n; i++) {
        const a = rand() * TAU;
        const e2 = rand() * 1.2 - 0.2;
        const sp = (4 + rand() * 14) * Math.sqrt(s);
        f.x = x;
        f.y = y + 1;
        f.z = z;
        f.vx = Math.cos(a) * Math.cos(e2) * sp;
        f.vy = Math.sin(e2) * sp + 3;
        f.vz = Math.sin(a) * Math.cos(e2) * sp;
        f.life = 0.7 + rand() * 1.1;
        f.s0 = 2.5 * s;
        f.s1 = (6 + rand() * 5) * s;
        p.fire.emit(f);
      }
      const k = boomSpark;
      for (let i = 0; i < n * 0.7; i++) {
        const a2 = rand() * TAU;
        const sp2 = 15 + rand() * 30;
        k.x = x;
        k.y = y + 1;
        k.z = z;
        k.vx = Math.cos(a2) * sp2;
        k.vy = 5 + rand() * 20;
        k.vz = Math.sin(a2) * sp2;
        k.life = 0.5 + rand() * 0.9;
        p.sparks.emit(k);
      }
      const m = boomSmoke;
      for (let i = 0; i < n * 0.5; i++) {
        m.x = x + (rand() - 0.5) * 4 * s;
        m.y = y + 2;
        m.z = z + (rand() - 0.5) * 4 * s;
        m.vx = (rand() - 0.5) * 3;
        m.vy = 3 + rand() * 4;
        m.vz = (rand() - 0.5) * 3 + 1;
        m.life = 2.5 + rand() * 2.5;
        m.s0 = 3 * s;
        m.s1 = (12 + rand() * 8) * s;
        p.smoke.emit(m);
      }
      if (y < 5) splash(x, z, s);
      light.x = x;
      light.y = y + LIGHT_LIFT;
      light.z = z;
      light.intensity = LIGHT_PER_BLAST * s;
      shake(s);
    },
    splash,
    /** concept splashAt (L1424–L1429). */
    spanSplash(x, z) {
      const sp = splashBig;
      for (let i = 0; i < 260; i++) {
        const a = rand() * TAU;
        const v = 3 + rand() * 12;
        sp.x = x + (rand() - 0.5) * 10;
        sp.z = z + (rand() - 0.5) * 8;
        sp.vx = Math.cos(a) * v;
        sp.vy = 10 + rand() * 22;
        sp.vz = Math.sin(a) * v;
        sp.life = 1.4 + rand() * 1.4;
        p.splash.emit(sp);
      }
    },
    /** concept flakBurst (L996–L999). */
    flak(x, y, z) {
      const m = flakSmoke;
      for (let i = 0; i < 9; i++) {
        m.x = x + (rand() - 0.5) * 3;
        m.y = y;
        m.z = z + (rand() - 0.5) * 3;
        m.vx = (rand() - 0.5) * 4;
        m.vy = rand() * 2;
        m.vz = (rand() - 0.5) * 4;
        m.life = 1.4 + rand();
        p.smoke.emit(m);
      }
      const f = flakFire;
      for (let i = 0; i < 12; i++) {
        f.x = x;
        f.y = y;
        f.z = z;
        f.vx = (rand() - 0.5) * 16;
        f.vy = (rand() - 0.5) * 16;
        f.vz = (rand() - 0.5) * 16;
        f.life = 0.35 + rand() * 0.25;
        p.fire.emit(f);
      }
    },
    /** concept enemyFire's flash (L994). */
    muzzle(x, y, z, vx, vz) {
      const k = muzzleSpark;
      k.x = x;
      k.y = y;
      k.z = z;
      k.vx = vx;
      k.vz = vz;
      p.sparks.emit(k);
    },
    /** concept hitBridge's sparks (L1063). */
    bridgeHit(x, y, z) {
      const k = hitSpark;
      for (let i = 0; i < 6; i++) {
        k.x = x;
        k.y = y;
        k.z = z;
        k.vx = (rand() - 0.5) * 20;
        k.vy = 4 + rand() * 10;
        k.vz = rand() * 10;
        k.life = 0.4 + rand() * 0.4;
        p.sparks.emit(k);
      }
    },
    /** concept updEnts' sinking smoke (L1022). */
    sinkSmoke(x, z, dt) {
      if (rand() < dt * 16) {
        const m = sinkSmokeSpec;
        m.x = x + (rand() - 0.5) * 4;
        m.z = z + (rand() - 0.5) * 4;
        m.vx = rand() - 0.5;
        m.vy = 3 + rand() * 2;
        m.life = 3 + rand() * 2;
        p.smoke.emit(m);
      }
    },
    /** concept updBridge's smoke (L1096). */
    bridgeSmoke(x, y, z, dt) {
      if (rand() < dt * 30) {
        const m = bridgeSmokeSpec;
        m.x = x + (rand() - 0.5) * 20;
        m.y = y;
        m.z = z + (rand() - 0.5) * 8;
        m.vx = rand() - 0.5;
        m.vy = 4 + rand() * 3;
        m.life = 3 + rand() * 2;
        p.smoke.emit(m);
      }
    },
    step(dt) {
      light.intensity *= Math.exp(-dt * LIGHT_FADE);
    },
    shiftZ(dz) {
      light.z += dz;
    },
  };
  return fx;
}
